export interface ParsedAccelerator {
  key: string;
  meta: boolean;
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
}

const MODIFIER_KEYS = new Set([
  "cmd",
  "command",
  "ctrl",
  "control",
  "cmdorctrl",
  "shift",
  "alt",
  "option",
]);

const SPECIAL_KEY_ALIASES: Record<string, string> = {
  space: " ",
  spacebar: " ",
  return: "enter",
  esc: "escape",
  plus: "+",
  minus: "-",
  del: "delete",
  backspace: "backspace",
  up: "arrowup",
  down: "arrowdown",
  left: "arrowleft",
  right: "arrowright",
};

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Parses an Electron-style accelerator string ("CmdOrCtrl+Shift+K") into a
 * normalised matcher. Case-insensitive; platform-aware CmdOrCtrl.
 */
export function parseAccelerator(accelerator: string): ParsedAccelerator {
  const parts = accelerator
    .split("+")
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);

  if (parts.length === 0) {
    throw new Error(`Invalid accelerator: ${accelerator}`);
  }

  let meta = false;
  let ctrl = false;
  let shift = false;
  let alt = false;
  let keyPart: string | null = null;

  for (const part of parts) {
    if (MODIFIER_KEYS.has(part)) {
      if (part === "cmd" || part === "command") meta = true;
      else if (part === "ctrl" || part === "control") ctrl = true;
      else if (part === "cmdorctrl") {
        if (IS_MAC) meta = true;
        else ctrl = true;
      } else if (part === "shift") shift = true;
      else if (part === "alt" || part === "option") alt = true;
    } else {
      keyPart = part;
    }
  }

  if (!keyPart) {
    throw new Error(`Accelerator missing key: ${accelerator}`);
  }

  const aliased = SPECIAL_KEY_ALIASES[keyPart] ?? keyPart;

  return { key: aliased, meta, ctrl, shift, alt };
}

/**
 * Returns true iff the given KeyboardEvent matches the parsed accelerator.
 * Key comparison is case-insensitive and matches event.key directly.
 */
export function matchesAccelerator(
  event: KeyboardEvent,
  parsed: ParsedAccelerator,
): boolean {
  if (event.metaKey !== parsed.meta) return false;
  if (event.ctrlKey !== parsed.ctrl) return false;
  if (event.shiftKey !== parsed.shift) return false;
  if (event.altKey !== parsed.alt) return false;
  return event.key.toLowerCase() === parsed.key.toLowerCase();
}
